import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'
import {
  Copy,
  ClipboardPaste,
  Download,
  Folder,
  FolderPlus,
  FileText,
  ImageIcon,
  Pencil,
  Scissors,
  Trash2,
  Upload,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { FileReaderOverlay } from '@/components/files/FileReaderOverlay'
import {
  downloadDataUrl,
  fileTypeLabel,
  formatFileSize,
  isImageName,
} from '@/lib/files'
import {
  workspaceService,
  type WorkspaceNode,
  type WorkspaceQuota,
} from '@/services/workspace'

export type WorkspaceServiceLike = {
  list(productCode: string, folderSlug: string, parentId: string | null): Promise<WorkspaceNode[]>
  getQuota(): Promise<WorkspaceQuota>
  getPath(productCode: string, folderSlug: string, nodeId: string | null): Promise<WorkspaceNode[]>
  createFolder(productCode: string, folderSlug: string, parentId: string | null, name?: string): Promise<WorkspaceNode>
  uploadFiles(productCode: string, folderSlug: string, parentId: string | null, files: any[]): Promise<WorkspaceNode[] & { uploadErrors?: Array<{ name: string; message: string }> }>
  rename(productCode: string, folderSlug: string, id: string, name: string): Promise<WorkspaceNode>
  remove(productCode: string, folderSlug: string, ids: string[]): Promise<void>
  move(sourceCode: string, sourceSlug: string, ids: string[], targetCode: string, targetSlug: string, targetParentId: string | null): Promise<void>
  copy(sourceCode: string, sourceSlug: string, ids: string[], targetCode: string, targetSlug: string, targetParentId: string | null): Promise<WorkspaceNode[]>
  cutToClipboard(productCode: string, folderSlug: string, ids: string[]): void
  copyToClipboard(productCode: string, folderSlug: string, ids: string[]): void
  getClipboard(): any
  paste(productCode: string, folderSlug: string, parentId: string | null): Promise<any>
}

type FileExplorerProps = {
  productCode: string
  folderSlug: string
  rootLabel: string
  service?: WorkspaceServiceLike
}

type MenuState = {
  x: number
  y: number
  targetId: string | null
}

function iconFor(node: WorkspaceNode) {
  if (node.kind === 'folder') {
    return <Folder className="h-4 w-4 text-amber-500" fill="currentColor" />
  }
  if (isImageName(node.name) && node.dataUrl) {
    return (
      <img
        src={node.dataUrl}
        alt=""
        className="h-4 w-4 rounded-[2px] object-cover"
      />
    )
  }
  if (isImageName(node.name)) {
    return <ImageIcon className="h-4 w-4 text-slate-500" />
  }
  return <FileText className="h-4 w-4 text-slate-500" />
}

export function FileExplorer({
  productCode,
  folderSlug,
  rootLabel,
  service,
}: FileExplorerProps) {
  const ws: WorkspaceServiceLike = service ?? workspaceService
  const containerRef = useRef<HTMLDivElement>(null)
  const uploadRef = useRef<HTMLInputElement>(null)
  const [parentId, setParentId] = useState<string | null>(null)
  const [items, setItems] = useState<WorkspaceNode[]>([])
  const [path, setPath] = useState<WorkspaceNode[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState('Sẵn sàng')
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [menu, setMenu] = useState<MenuState | null>(null)
  const [dropTarget, setDropTarget] = useState<string | 'root' | null>(null)
  const [clipboardTick, setClipboardTick] = useState(0)
  const [previewId, setPreviewId] = useState<string | null>(null)
  const [quota, setQuota] = useState<WorkspaceQuota | null>(null)

  const clipboard = ws.getClipboard()
  const canPaste = Boolean(clipboard?.ids.length)
  const quotaFull = Boolean(
    quota && quota.usedBytes >= quota.maxTotalBytes,
  )

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [listed, crumbs, nextQuota] = await Promise.all([
        ws.list(productCode, folderSlug, parentId),
        ws.getPath(productCode, folderSlug, parentId),
        ws.getQuota(),
      ])
      setItems(listed)
      setPath(crumbs)
      setQuota(nextQuota)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không tải được thư mục.')
    } finally {
      setLoading(false)
    }
  }, [productCode, folderSlug, parentId, ws])

  useEffect(() => {
    void refresh()
    setSelected([])
    setRenamingId(null)
    setMenu(null)
    setPreviewId(null)
  }, [refresh])

  useEffect(() => {
    function close() {
      setMenu(null)
    }
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [])

  const selectedItems = useMemo(
    () => items.filter((item) => selected.includes(item.id)),
    [items, selected],
  )

  const images = useMemo(
    () => items.filter((item) => item.kind === 'file' && isImageName(item.name)),
    [items],
  )

  const previewFile = useMemo(() => {
    if (previewId) {
      return items.find((item) => item.id === previewId) ?? null
    }
    if (selected.length === 1) {
      return items.find((item) => item.id === selected[0]) ?? null
    }
    return null
  }, [items, previewId, selected])

  const readerOpen = Boolean(previewId && previewFile?.kind === 'file')

  function selectOne(id: string, additive: boolean, range: boolean) {
    if (range && selected[0]) {
      const start = items.findIndex((i) => i.id === selected[0])
      const end = items.findIndex((i) => i.id === id)
      if (start >= 0 && end >= 0) {
        const [from, to] = start < end ? [start, end] : [end, start]
        setSelected(items.slice(from, to + 1).map((i) => i.id))
        return
      }
    }
    if (additive) {
      setSelected((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      )
      return
    }
    setSelected([id])
  }

  async function withStatus(action: () => Promise<void>, message: string) {
    try {
      await action()
      setStatus(message)
      setClipboardTick((n) => n + 1)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Thao tác thất bại.')
    }
  }

  async function createFolder() {
    await withStatus(async () => {
      const folder = await ws.createFolder(
        productCode,
        folderSlug,
        parentId,
      )
      setSelected([folder.id])
      setRenamingId(folder.id)
      setRenameValue(folder.name)
    }, 'Đã tạo thư mục')
  }

  async function handleUpload(fileList: FileList | null, intoId: string | null = parentId) {
    if (!fileList?.length) return
    if (quotaFull) {
      setError(
        `Đã hết dung lượng lưu trữ file (${formatFileSize(quota?.maxTotalBytes)}). Xóa bớt file trước khi tải lên.`,
      )
      return
    }
    const files = [...fileList].map((file) => ({
      name: file.name,
      mimeType: file.type || undefined,
      size: file.size,
      file,
    }))
    let uploadErrors: Array<{ name: string; message: string }> = []
    await withStatus(async () => {
      const uploaded = await ws.uploadFiles(productCode, folderSlug, intoId, files)
      uploadErrors = uploaded.uploadErrors ?? []
    }, `Đã tải lên ${files.length} file`)
    if (uploadErrors.length > 0) {
      setError(uploadErrors.map(({ name, message }) => `${name}: ${message}`).join(' '))
    }
  }

  function copySelected() {
    if (!selected.length) return
    ws.copyToClipboard(productCode, folderSlug, selected)
    setClipboardTick((n) => n + 1)
    setStatus(`Đã sao chép ${selected.length} mục`)
  }

  function cutSelected() {
    if (!selected.length) return
    ws.cutToClipboard(productCode, folderSlug, selected)
    setClipboardTick((n) => n + 1)
    setStatus(`Đã cắt ${selected.length} mục`)
  }

  async function pasteHere(targetFolderId: string | null = parentId) {
    if (!canPaste) return
    await withStatus(async () => {
      await ws.paste(productCode, folderSlug, targetFolderId)
    }, 'Đã dán')
  }

  async function deleteSelected() {
    if (!selected.length) return
    const ok = window.confirm(`Xóa ${selected.length} mục đã chọn?`)
    if (!ok) return
    await withStatus(async () => {
      await ws.remove(productCode, folderSlug, selected)
      setSelected([])
    }, 'Đã xóa')
  }

  async function commitRename() {
    if (!renamingId) return
    const value = renameValue.trim()
    setRenamingId(null)
    if (!value) return
    await withStatus(async () => {
      await ws.rename(productCode, folderSlug, renamingId, value)
    }, 'Đã đổi tên')
  }

  function downloadNodes(nodes: WorkspaceNode[]) {
    const files = nodes.filter((node) => node.kind === 'file')
    if (!files.length) return
    const ready = files.filter((file) => file.dataUrl)
    const missing = files.length - ready.length
    void Promise.all(
      ready.map((file) => downloadDataUrl(file.name, file.dataUrl as string)),
    ).then(() => {
      if (missing) {
        setError('Một số file chưa có nội dung để tải về. Hãy tải file lên trước.')
        return
      }
      setStatus(`Đã tải về ${ready.length} file`)
    })
  }

  function downloadSelected() {
    downloadNodes(selectedItems)
  }

  function openItem(node: WorkspaceNode) {
    if (node.kind === 'folder') {
      setParentId(node.id)
      setPreviewId(null)
      return
    }
    setSelected([node.id])
    setPreviewId(node.id)
  }

  function showImage(file: WorkspaceNode) {
    setSelected([file.id])
    setPreviewId(file.id)
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (renamingId) {
      if (e.key === 'Enter') {
        e.preventDefault()
        void commitRename()
      }
      if (e.key === 'Escape') setRenamingId(null)
      return
    }
    if (e.ctrlKey && e.key.toLowerCase() === 'c') {
      e.preventDefault()
      copySelected()
    }
    if (e.ctrlKey && e.key.toLowerCase() === 'x') {
      e.preventDefault()
      cutSelected()
    }
    if (e.ctrlKey && e.key.toLowerCase() === 'v') {
      e.preventDefault()
      void pasteHere()
    }
    if (e.ctrlKey && e.key.toLowerCase() === 'a') {
      e.preventDefault()
      setSelected(items.map((i) => i.id))
    }
    if (e.key === 'Delete') {
      e.preventDefault()
      void deleteSelected()
    }
    if (e.key === 'F2' && selected.length === 1) {
      e.preventDefault()
      const node = items.find((i) => i.id === selected[0])
      if (node) {
        setRenamingId(node.id)
        setRenameValue(node.name)
      }
    }
    if (e.key === 'Enter' && selected.length === 1) {
      const node = items.find((i) => i.id === selected[0])
      if (node) openItem(node)
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      const current =
        previewFile && isImageName(previewFile.name) ? previewFile : null
      if (current && images.length > 1) {
        e.preventDefault()
        const index = images.findIndex((item) => item.id === current.id)
        const delta = e.key === 'ArrowRight' ? 1 : -1
        const next = images[(index + delta + images.length) % images.length]
        showImage(next)
      }
    }
    if (e.key === 'Backspace' && parentId) {
      e.preventDefault()
      const parent = path[path.length - 1]
      setParentId(parent?.parentId ?? null)
    }
  }

  function openMenu(e: MouseEvent, targetId: string | null) {
    e.preventDefault()
    e.stopPropagation()
    if (targetId && !selected.includes(targetId)) setSelected([targetId])
    if (!targetId) setSelected([])
    setMenu({ x: e.clientX, y: e.clientY, targetId })
  }

  async function dropOn(target: string | 'root', e: DragEvent) {
    e.preventDefault()
    setDropTarget(null)
    const intoId = target === 'root' ? parentId : target
    if (e.dataTransfer.files.length) {
      await handleUpload(e.dataTransfer.files, intoId)
      return
    }
    const dragged = e.dataTransfer.getData('application/x-erp-ids')
    if (!dragged) return
    const ids: string[] = JSON.parse(dragged)
    await withStatus(async () => {
      await ws.move(
        productCode,
        folderSlug,
        ids,
        productCode,
        folderSlug,
        intoId,
      )
      setSelected([])
    }, 'Đã di chuyển')
  }

  void clipboardTick

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className={`flex flex-col border-[var(--color-border)] bg-[var(--color-surface)] outline-none ${
        readerOpen
          ? 'fixed top-[var(--topbar-height)] right-0 bottom-0 left-[var(--sidebar-width)] z-40 border-l'
          : 'h-[min(680px,calc(100vh-10rem))] min-h-[560px] rounded-[var(--radius-md)] border'
      }`}
      onKeyDown={onKeyDown}
      onClick={() => containerRef.current?.focus()}
    >
      <input
        ref={uploadRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          void handleUpload(e.target.files)
          e.target.value = ''
        }}
      />

      <div className="flex flex-wrap items-center gap-1 border-b border-[var(--color-border)] bg-slate-50 px-2 py-1.5">
        <Button size="sm" variant="secondary" onClick={() => void createFolder()}>
          <FolderPlus className="h-3.5 w-3.5" />
          Thư mục mới
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={quotaFull}
          title={
            quotaFull
              ? 'Đã hết dung lượng lưu trữ file — xóa bớt trước khi tải lên'
              : undefined
          }
          onClick={() => uploadRef.current?.click()}
        >
          <Upload className="h-3.5 w-3.5" />
          Tải lên
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={selectedItems.length !== 1 || selectedItems[0]?.kind !== 'file'}
          onClick={() => {
            const node = selectedItems[0]
            if (node) openItem(node)
          }}
        >
          <FileText className="h-3.5 w-3.5" />
          Mở
        </Button>
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <Button size="sm" variant="ghost" disabled={!selected.length} onClick={cutSelected}>
          <Scissors className="h-3.5 w-3.5" />
          Cắt
        </Button>
        <Button size="sm" variant="ghost" disabled={!selected.length} onClick={copySelected}>
          <Copy className="h-3.5 w-3.5" />
          Sao chép
        </Button>
        <Button size="sm" variant="ghost" disabled={!canPaste} onClick={() => void pasteHere()}>
          <ClipboardPaste className="h-3.5 w-3.5" />
          Dán
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={selected.length !== 1}
          onClick={() => {
            const node = selectedItems[0]
            if (!node) return
            setRenamingId(node.id)
            setRenameValue(node.name)
          }}
        >
          <Pencil className="h-3.5 w-3.5" />
          Đổi tên
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={!selectedItems.some((item) => item.kind === 'file')}
          onClick={downloadSelected}
        >
          <Download className="h-3.5 w-3.5" />
          Tải về
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={!selected.length}
          onClick={() => void deleteSelected()}
        >
          <Trash2 className="h-3.5 w-3.5" />
          Xóa
        </Button>
      </div>

      {quota?.warn ? (
        <div className="border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900">
          {quotaFull
            ? `Đã hết dung lượng lưu trữ file (${formatFileSize(quota.usedBytes)} / ${formatFileSize(quota.maxTotalBytes)}). Không thể tải thêm — xóa bớt file để giải phóng chỗ.`
            : `Sắp hết dung lượng lưu trữ file (${formatFileSize(quota.usedBytes)} / ${formatFileSize(quota.maxTotalBytes)}). Nên xóa bớt file không cần thiết.`}
        </div>
      ) : null}

      <div className="flex items-center gap-1 border-b border-[var(--color-border)] px-3 py-1.5 text-xs">
        <button
          type="button"
          className="font-medium text-[var(--color-accent)] hover:underline"
          onClick={() => setParentId(null)}
        >
          {rootLabel}
        </button>
        {path.map((crumb) => (
          <span key={crumb.id} className="flex items-center gap-1">
            <span className="text-slate-400">›</span>
            <button
              type="button"
              className="font-medium text-[var(--color-text)] hover:underline"
              onClick={() => setParentId(crumb.id)}
            >
              {crumb.name}
            </button>
          </span>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
      <div
        className={`min-h-0 overflow-auto ${
          readerOpen
            ? 'h-40 shrink-0 border-b border-[var(--color-border)] lg:h-auto lg:w-[300px] lg:border-r lg:border-b-0'
            : 'flex-1'
        } ${dropTarget === 'root' ? 'bg-[var(--color-accent-soft)]' : ''}`}
        onContextMenu={(e) => openMenu(e, null)}
        onDragOver={(e) => {
          e.preventDefault()
          setDropTarget('root')
        }}
        onDragLeave={() => setDropTarget((cur) => (cur === 'root' ? null : cur))}
        onDrop={(e) => void dropOn('root', e)}
        onClick={() => setSelected([])}
      >
        <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 bg-slate-50 text-left text-xs font-semibold tracking-wide text-slate-500 uppercase">
            <tr className="border-b border-[var(--color-border)]">
              <th className="px-3 py-2">Tên</th>
              {readerOpen ? null : (
                <>
                  <th className="w-36 px-3 py-2">Ngày sửa</th>
                  <th className="w-28 px-3 py-2">Loại</th>
                  <th className="w-24 px-3 py-2 text-right">Kích thước</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={readerOpen ? 1 : 4} className="px-3 py-10 text-center text-slate-500">
                  Đang tải...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={readerOpen ? 1 : 4}>
                  <EmptyState
                    title="Thư mục trống"
                    description="Tạo thư mục mới, tải file lên, hoặc dán từ clipboard."
                  />
                </td>
              </tr>
            ) : (
              items.map((node) => {
                const active = selected.includes(node.id)
                const cutGhost =
                  clipboard?.operation === 'cut' && clipboard.ids.includes(node.id)
                return (
                  <tr
                    key={node.id}
                    draggable
                    className={`border-b border-slate-100 last:border-0 ${
                      active ? 'bg-[var(--color-accent-soft)]' : 'hover:bg-slate-50'
                    } ${dropTarget === node.id ? 'outline outline-1 outline-[var(--color-accent)]' : ''} ${
                      cutGhost ? 'opacity-50' : ''
                    }`}
                    style={{ height: 'var(--table-row-h)' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      selectOne(node.id, e.ctrlKey || e.metaKey, e.shiftKey)
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation()
                      openItem(node)
                    }}
                    onContextMenu={(e) => openMenu(e, node.id)}
                    onDragStart={(e) => {
                      const ids = selected.includes(node.id) ? selected : [node.id]
                      if (!selected.includes(node.id)) setSelected(ids)
                      e.dataTransfer.setData('application/x-erp-ids', JSON.stringify(ids))
                      e.dataTransfer.effectAllowed = 'move'
                    }}
                    onDragOver={(e) => {
                      if (node.kind !== 'folder') return
                      e.preventDefault()
                      e.stopPropagation()
                      setDropTarget(node.id)
                    }}
                    onDrop={(e) => {
                      if (node.kind !== 'folder') return
                      e.stopPropagation()
                      void dropOn(node.id, e)
                    }}
                  >
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-2">
                        {iconFor(node)}
                        {renamingId === node.id ? (
                          <input
                            autoFocus
                            value={renameValue}
                            className="h-7 w-full rounded-[var(--radius-sm)] border border-[var(--color-accent)] px-1.5 text-sm outline-none"
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={() => void commitRename()}
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <span className="truncate">{node.name}</span>
                        )}
                      </div>
                    </td>
                    {readerOpen ? null : (
                      <>
                    <td className="px-3 py-1.5 text-[var(--color-text-muted)]">
                      {node.updatedAt}
                    </td>
                    <td className="px-3 py-1.5 text-[var(--color-text-muted)]">
                      {fileTypeLabel(node.name, node.kind)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-[var(--color-text-muted)]">
                      {node.kind === 'file' ? formatFileSize(node.size) : ''}
                    </td>
                      </>
                    )}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
        </div>
      </div>
      {readerOpen && previewFile ? (
        <FileReaderOverlay
          file={previewFile}
          images={images}
          onClose={() => setPreviewId(null)}
          onShowImage={showImage}
          onDownload={(file) => downloadNodes([file])}
        />
      ) : null}
      </div>

      <div className="flex items-center justify-between border-t border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-text-muted)]">
        <span>
          {items.length} mục
          {selected.length ? ` · ${selected.length} đã chọn` : ''}
          {quota
            ? ` · lưu trữ ${formatFileSize(quota.usedBytes)} / ${formatFileSize(quota.maxTotalBytes)}`
            : ''}
        </span>
        <span className={error ? 'text-red-600' : undefined}>{error ?? status}</span>
      </div>

      {menu ? (
        <ul
          className="fixed z-50 min-w-44 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white py-1 text-sm shadow-md"
          style={{ left: menu.x, top: menu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          {menu.targetId ? (
            <li>
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left hover:bg-slate-50"
                onClick={() => {
                  const node = items.find((i) => i.id === menu.targetId)
                  if (node) openItem(node)
                  setMenu(null)
                }}
              >
                Mở
              </button>
            </li>
          ) : null}
          {menu.targetId ? (
            <li>
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left hover:bg-slate-50 disabled:text-slate-300"
                disabled={items.find((i) => i.id === menu.targetId)?.kind !== 'file'}
                onClick={() => {
                  const node = items.find((i) => i.id === menu.targetId)
                  if (node?.kind === 'file') downloadNodes([node])
                  setMenu(null)
                }}
              >
                Tải về
              </button>
            </li>
          ) : null}
          <li>
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left hover:bg-slate-50"
              onClick={() => {
                setMenu(null)
                void createFolder()
              }}
            >
              Thư mục mới
            </button>
          </li>
          <li>
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left hover:bg-slate-50"
              onClick={() => {
                setMenu(null)
                uploadRef.current?.click()
              }}
            >
              Tải lên...
            </button>
          </li>
          <li className="my-1 border-t border-slate-100" />
          <li>
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left hover:bg-slate-50 disabled:text-slate-300"
              disabled={!selected.length}
              onClick={() => {
                cutSelected()
                setMenu(null)
              }}
            >
              Cắt
            </button>
          </li>
          <li>
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left hover:bg-slate-50 disabled:text-slate-300"
              disabled={!selected.length}
              onClick={() => {
                copySelected()
                setMenu(null)
              }}
            >
              Sao chép
            </button>
          </li>
          <li>
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left hover:bg-slate-50 disabled:text-slate-300"
              disabled={!canPaste}
              onClick={() => {
                const target = menu.targetId
                const folder = items.find((i) => i.id === target && i.kind === 'folder')
                setMenu(null)
                void pasteHere(folder ? folder.id : parentId)
              }}
            >
              Dán
            </button>
          </li>
          <li>
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left hover:bg-slate-50 disabled:text-slate-300"
              disabled={selected.length !== 1}
              onClick={() => {
                const node = selectedItems[0]
                if (node) {
                  setRenamingId(node.id)
                  setRenameValue(node.name)
                }
                setMenu(null)
              }}
            >
              Đổi tên
            </button>
          </li>
          <li className="my-1 border-t border-slate-100" />
          <li>
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left text-red-600 hover:bg-red-50 disabled:text-slate-300"
              disabled={!selected.length}
              onClick={() => {
                setMenu(null)
                void deleteSelected()
              }}
            >
              Xóa
            </button>
          </li>
        </ul>
      ) : null}
    </div>
  )
}
